import supabase from '../services/supabase';

/**
 * Create a new place record
 * @param {Object} placeData - Place data with lat, lng, name, etc.
 * @returns {Promise<Object>} - The newly created place
 */
export const createPlace = async (placeData) => {
  try {
    const { data, error } = await supabase
      .from('places')
      .insert([placeData])
      .select()
      .single();
      
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error creating place:', error);
    throw error;
  }
};

/**
 * Get a place by ID
 * @param {string} placeId - ID of the place to retrieve
 * @returns {Promise<Object>} - The place data
 */
export const getPlaceById = async (placeId) => {
  try {
    const { data, error } = await supabase
      .from('places')
      .select('*')
      .eq('id', placeId)
      .single();
      
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error fetching place:', error);
    throw error;
  }
};

/**
 * Get places for multiple posts
 * @param {Array<string>} placeIds - Array of place IDs
 * @returns {Promise<Array>} - Array of places
 */
export const getPlacesByIds = async (placeIds) => {
  if (!placeIds || placeIds.length === 0) return [];
  
  try {
    const { data, error } = await supabase
      .from('places')
      .select('*')
      .in('id', placeIds);
      
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Error fetching places:', error);
    throw error;
  }
};

/**
 * Search for places by name
 * @param {string} query - Search query
 * @param {number} limit - Maximum number of results
 * @returns {Promise<Array>} - Array of matching places
 */
export const searchPlaces = async (query, limit = 10) => {
  try {
    const { data, error } = await supabase
      .from('places')
      .select('*')
      .ilike('name', `%${query}%`)
      .limit(limit);
      
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Error searching places:', error);
    throw error;
  }
};

/**
 * Update a place
 * @param {string} placeId - ID of the place to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} - The updated place
 */
export const updatePlace = async (placeId, updates) => {
  try {
    const { data, error } = await supabase
      .from('places')
      .update(updates)
      .eq('id', placeId)
      .select()
      .single();
      
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('Error updating place:', error);
    throw error;
  }
};

/**
 * Delete a place
 * @param {string} placeId - ID of the place to delete
 * @returns {Promise<void>}
 */
export const deletePlace = async (placeId) => {
  try {
    const { error } = await supabase
      .from('places')
      .delete()
      .eq('id', placeId);
      
    if (error) throw error;
  } catch (error) {
    console.error('Error deleting place:', error);
    throw error;
  }
}; 